/**
 * react-admin auth provider backed by Firebase Phone Authentication.
 */
import { IS_DEMO } from './demoData';
import { clearAuth, getAuth, isAdminSession, setAuth } from './authStorage';
import { API_BASE_URL } from './config';

const DEMO_AUTH = {
    token: 'demo-token',
    user: { _id: 'demo', name: 'Demo Admin', phone: '+971****0000', role: 'admin', status: 'active' },
};

const MESSAGES = {
    notAdmin: 'This account is not authorized for admin access.',
    checkFailed: 'Authentication failed. Please try again.',
    generic: "We couldn't process that request. Please try again.",
    network: "We couldn't reach the server. Check your connection and try again.",
};

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

const signOutFirebase = async () => {
    try {
        const { auth } = require('./firebase');
        if (auth && auth.currentUser) {
            await auth.signOut();
        }
    } catch (e) {
        // Best-effort
    }
};

const authProvider = {
    login: async ({ idToken } = {}) => {
        if (IS_DEMO) {
            setAuth(DEMO_AUTH);
            return DEMO_AUTH;
        }

        if (!idToken) {
            clearAuth();
            throw new Error(MESSAGES.checkFailed);
        }

        let response;
        try {
            response = await fetch(`${API_BASE_URL}/authenticateFirebase`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify({ idToken }),
            });
        } catch (error) {
            clearAuth();
            await signOutFirebase();
            throw new Error(MESSAGES.network);
        }

        let body;
        try {
            body = await response.json();
        } catch (error) {
            body = null;
        }

        if (response.status === 429) {
            clearAuth();
            await signOutFirebase();
            const retryAfter =
                (body && (Number(body.retry_after) || Number(body.retryAfter))) || null;
            const err = new Error(MESSAGES.generic);
            if (retryAfter && Number.isFinite(retryAfter) && retryAfter > 0) {
                err.retryAfter = Math.ceil(retryAfter);
            }
            throw err;
        }

        if (!response.ok) {
            clearAuth();
            await signOutFirebase();
            const backendMsg = body && body.errors && body.errors.msg;
            if (backendMsg === 'ACCOUNT_BANNED') throw new Error('This account has been banned.');
            if (backendMsg === 'ACCOUNT_DELETED') throw new Error('This account has been deleted.');
            if (backendMsg === 'ACCOUNT_INACTIVE') throw new Error('This account is inactive. Please contact support.');
            throw new Error(MESSAGES.notAdmin);
        }

        if (!isAdminSession(body)) {
            clearAuth();
            await signOutFirebase();
            const userRole = body && body.user && body.user.role;
            if (userRole && userRole !== 'admin') {
                throw new Error('This account does not have admin access.');
            }
            throw new Error(MESSAGES.notAdmin);
        }

        setAuth(body);
        return body;
    },

    logout: async () => {
        clearAuth();
        if (!IS_DEMO) {
            await signOutFirebase();
        }
    },

    /**
     * Only 401/403 mean "this session is no longer valid".
     * DEBUG: alert to catch what API call is triggering logout.
     */
    checkError: ({ status } = {}) => {
        if (IS_DEMO) return Promise.resolve();
        if (status === 401 || status === 403) {
            clearAuth();
            return Promise.reject();
        }
        return Promise.resolve();
    },

    checkAuth: () => {
        if (IS_DEMO) return Promise.resolve();
        const auth = getAuth();
        if (!isAdminSession(auth)) {
            clearAuth();
            return Promise.reject();
        }
        return Promise.resolve();
    },

    getPermissions: () => {
        if (IS_DEMO) return Promise.resolve({ role: 'admin', adminRoles: ['SUPER_ADMIN'] });
        const auth = getAuth();
        if (!isAdminSession(auth)) return Promise.reject();
        return Promise.resolve({
            role: auth.user.role,
            adminRoles: auth.user.adminRoles || []
        });
    },

    getIdentity: () => {
        if (IS_DEMO) {
            return Promise.resolve({ id: 'demo', fullName: 'Demo Admin' });
        }
        const auth = getAuth();
        if (!isAdminSession(auth)) {
            return Promise.reject();
        }
        const { user } = auth;
        return Promise.resolve({
            id: user._id || user.id || user.phone,
            fullName: user.name || user.phone,
        });
    },
};

export default authProvider;
